import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { fetchCharacters, submitRsvp } from '../../lib/api';
import type { CharacterRow } from '../../lib/types';
import {
  canContinueChild,
  emptyChild,
  findById,
  formatSubmitError,
  lockedCharacterIds,
  toRsvpInput,
  withAvailability,
} from './lib';
import type { DraftChild, ParentContact } from './lib';

const STORAGE_KEY = 'rsvp:v1:draft';

interface PersistedState {
  parent: ParentContact;
  children: DraftChild[];
  draft: DraftChild;
  parentEverLocked: boolean;
}

const emptyParent = (): ParentContact => ({
  parent_name: '',
  parent_email: '',
  parent_phone: '',
});

function loadPersisted(): PersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.draft || !parsed.parent || !Array.isArray(parsed.children)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPersisted(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — best effort
  }
}

export interface RsvpFlow {
  characters: CharacterRow[];
  parent: ParentContact;
  children: DraftChild[];
  draft: DraftChild;
  submitting: boolean;
  globalError: string | null;
  parentLocked: boolean;
  pickerCharacters: CharacterRow[];
  characterById: (id: number | null) => CharacterRow | null;

  setParent: (p: ParentContact) => void;
  setDraft: (d: DraftChild) => void;
  resetDraft: () => void;
  commitDraft: (overrides?: Partial<DraftChild>) => Promise<boolean>;
  editChild: (draftId: string) => void;
  submitAll: () => Promise<void>;
}

export function useRsvpFlow(): RsvpFlow {
  const navigate = useNavigate();

  const initial = useMemo<PersistedState>(() => {
    const persisted = loadPersisted();
    if (persisted) return persisted;
    return {
      parent: emptyParent(),
      children: [],
      draft: emptyChild(),
      parentEverLocked: false,
    };
  }, []);

  const [characters, setCharacters] = useState<CharacterRow[]>([]);
  const [parent, setParent] = useState<ParentContact>(initial.parent);
  const [children, setChildren] = useState<DraftChild[]>(initial.children);
  const [draft, setDraft] = useState<DraftChild>(initial.draft);
  const [parentEverLocked, setParentEverLocked] = useState<boolean>(initial.parentEverLocked);
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  // Sticky lock: once we've committed a child with parent info, parent fields stay read-only.
  useEffect(() => {
    if (!parentEverLocked && children.length > 0) {
      setParentEverLocked(true);
    }
  }, [children.length, parentEverLocked]);

  // Persist on any meaningful state change.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const snapshot: PersistedState = { parent, children, draft, parentEverLocked };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // quota or privacy mode — silently skip
    }
  }, [parent, children, draft, parentEverLocked]);

  // Load characters once; cancel setState if unmounted mid-fetch.
  useEffect(() => {
    let cancelled = false;
    fetchCharacters()
      .then((rows) => {
        if (!cancelled) setCharacters(rows);
      })
      .catch(() => {
        if (!cancelled) setGlobalError('Could not load fighters. Refresh to try again.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const characterById = useCallback(
    (id: number | null) => findById(characters, id),
    [characters],
  );

  const locked = useMemo(() => lockedCharacterIds(children), [children]);
  const pickerCharacters = useMemo(
    () => withAvailability(characters, locked, draft.character_id),
    [characters, locked, draft.character_id],
  );

  // Synchronous persist helper
  const persist = useCallback(
    (snapshot: PersistedState) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      } catch {
        // quota or privacy mode — silently skip
      }
    },
    [],
  );

  const resetDraft = useCallback(() => {
    setDraft(emptyChild());
    // Persist immediately
    persist({ parent, children, draft: emptyChild(), parentEverLocked });
  }, [parent, children, persist]);

  const commitDraft = useCallback(
    async (overrides?: Partial<DraftChild>): Promise<boolean> => {
      const candidate = overrides ? { ...draft, ...overrides } : draft;
      if (!canContinueChild(candidate, parent)) return false;
      if (candidate.status === 'coming' && candidate.character_id === null) return false;

      setSubmitting(true);
      setGlobalError(null);

      try {
        const res = await submitRsvp(toRsvpInput(candidate, parent));
        if (res.ok) {
          // Save succeeded — add to children with submitted: true
          const submitted = { ...candidate, submitted: true, error: null };
          const newChildren = [...children, submitted];
          setChildren(newChildren);
          // Reset the draft
          const newDraft = emptyChild();
          setDraft(newDraft);
          // Persist synchronously before returning
          persist({ parent, children: newChildren, draft: newDraft, parentEverLocked });
          setSubmitting(false);
          return true;
        } else {
          // Save failed
          setGlobalError(formatSubmitError(res.error, res.issues));
          if (res.error === 'character_taken') {
            try {
              const rows = await fetchCharacters();
              setCharacters(rows);
            } catch {
              // Refetch failed; error already surfaced
            }
          }
          setSubmitting(false);
          return false;
        }
      } catch {
        setGlobalError('Failed to save RSVP. Please try again.');
        setSubmitting(false);
        return false;
      }
    },
    [draft, parent, children, parentEverLocked, persist],
  );

  const editChild = useCallback((draftId: string) => {
    setChildren((prev) => {
      const target = prev.find((c) => c.draftId === draftId);
      if (!target) return prev;
      setDraft(target);
      const newChildren = prev.filter((c) => c.draftId !== draftId);
      // Persist immediately
      persist({ parent, children: newChildren, draft: target, parentEverLocked });
      return newChildren;
    });
  }, [parent, parentEverLocked, persist]);

  const submitAll = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setGlobalError(null);

    const snapshot = [...children];
    const updated = [...snapshot];
    let allOk = true;

    for (let i = 0; i < updated.length; i++) {
      const c = updated[i];
      if (c.submitted) continue;
      const res = await submitRsvp(toRsvpInput(c, parent));
      if (res.ok) {
        updated[i] = { ...c, submitted: true, error: null };
      } else {
        allOk = false;
        updated[i] = { ...c, error: formatSubmitError(res.error, res.issues) };
        if (res.error === 'character_taken') {
          try {
            const rows = await fetchCharacters();
            setCharacters(rows);
          } catch {
            // Refetch failed; surfaced via per-row error.
          }
        }
      }
    }

    setChildren(updated);
    setSubmitting(false);
    submittingRef.current = false;

    if (!allOk) {
      setGlobalError('Some RSVPs need attention.');
      return;
    }

    const first = snapshot[0];
    clearPersisted();
    navigate({
      to: '/confirmation',
      search: {
        status: first?.status ?? 'coming',
        character: characterById(first?.character_id ?? null)?.name ?? '',
      },
    });
  }, [children, parent, characterById, navigate]);

  return useMemo<RsvpFlow>(
    () => ({
      characters,
      parent,
      children,
      draft,
      submitting,
      globalError,
      parentLocked: parentEverLocked,
      pickerCharacters,
      characterById,
      setParent,
      setDraft,
      resetDraft,
      commitDraft,
      editChild,
      submitAll,
    }),
    [
      characters,
      parent,
      children,
      draft,
      submitting,
      globalError,
      parentEverLocked,
      pickerCharacters,
      characterById,
      resetDraft,
      commitDraft,
      editChild,
      submitAll,
    ],
  );
}

export function hasInProgressRoster(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { children?: unknown };
    return Array.isArray(parsed?.children) && parsed.children.length > 0;
  } catch {
    return false;
  }
}
